import axios from "axios";
import { getCookie } from "../../../utility/getCookie";

export type TicketSeverity = "High" | "Medium" | "Low";
export type TicketStatus = "Open" | "In Progress" | "Resolved" | "Closed";
export type TicketSender = "admin" | "user";
export type AttachmentKind = "image" | "video" | "file";

export interface TicketAttachment {
    id: string;
    name: string;
    size: string;
    kind: AttachmentKind;
    url?: string;
}

export interface TicketMessage {
    id: string;
    sender: TicketSender;
    senderName: string;
    sentAt: string;
    body: string;
    attachments?: TicketAttachment[];
}

export interface SupportTicket {
    serialNo: number;
    ticketId: string;
    openedAt: string;
    openedBy: string;
    category: string;
    subject: string;
    severity: TicketSeverity;
    status: TicketStatus;
    language: string;
    messages: TicketMessage[];
}

export interface SelectOption {
    value: string;
    label: string;
    hint?: string;
}

export interface DateRangeValue {
    from: string;
    to: string;
}

const CHAT_API_URL =
    process.env.REACT_APP_CHAT_SERVICE_URL ||
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/chat`;

const api = axios.create({
    baseURL: CHAT_API_URL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use(
    (config) => {
        const token =
            localStorage.getItem("access_token") ||
            getCookie("access_token");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

// ── API row types ────────────────────────────────────────────────

interface ApiTicket {
    ticketId: string;
    conversationId: string;
    customerName?: string;
    category?: string;
    subject?: string;
    reason?: string;
    priority?: "urgent" | "high" | "normal" | "low";
    status?:
        | "open"
        | "in_progress"
        | "resolved"
        | "pending"
        | "escalated"
        | "closed";
    language?: string;
    createdAt?: string;
}

interface ApiMessage {
    _id: string;
    sender: "user" | "bot" | "agent" | "system" | "note";
    senderName?: string;
    text: string;
    fileUrl?: string;
    attachments?: Array<{
        url: string;
        fileName: string;
        fileType: string;
        fileSize?: number;
    }>;
    createdAt?: string;
}

// ── Mappers ──────────────────────────────────────────────────────

const mapSeverity = (priority?: ApiTicket["priority"]): TicketSeverity => {
    if (priority === "urgent" || priority === "high") return "High";
    if (priority === "low") return "Low";
    return "Medium";
};

const mapStatus = (status?: ApiTicket["status"]): TicketStatus => {
    if (status === "resolved") return "Resolved";
    if (status === "closed") return "Closed";
    if (status === "open") return "Open";
    return "In Progress";
};

const formatSentAt = (iso?: string) =>
    iso
        ? new Date(iso).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "";

const mapAttachment = (
    a: { url: string; fileName: string; fileType: string; fileSize?: number },
    index: number,
): TicketAttachment => ({
    id: `att-${index}-${a.fileName}`,
    name: a.fileName,
    size: a.fileSize ? formatFileSize(a.fileSize) : "",
    kind: a.fileType?.startsWith("image/")
        ? "image"
        : a.fileType?.startsWith("video/")
          ? "video"
          : "file",
    url: a.url,
});

const mapMessage = (m: ApiMessage): TicketMessage => ({
    id: m._id,
    sender: m.sender === "user" ? "user" : "admin",
    senderName:
        m.sender === "user"
            ? m.senderName || "You"
            : m.senderName || "Salar Support",
    sentAt: formatSentAt(m.createdAt),
    body: m.text,
    attachments: m.fileUrl
        ? [
              {
                  id: `${m._id}-file`,
                  name: m.fileUrl.split("/").pop() || "Attachment",
                  size: "",
                  kind: "image",
                  url: m.fileUrl,
              },
          ]
        : m.attachments?.map(mapAttachment),
});

const mapTicket = (t: ApiTicket, index: number): SupportTicket => ({
    serialNo: index + 1,
    ticketId: t.ticketId,
    openedAt: t.createdAt || "",
    openedBy: t.customerName || "Customer",
    category: t.category || "Other",
    subject: t.subject || t.reason || "Support request",
    severity: mapSeverity(t.priority),
    status: mapStatus(t.status),
    language: t.language || "English",
    messages: [],
});

// ── API functions ────────────────────────────────────────────────

export const listMyTickets = async (): Promise<SupportTicket[]> => {
    const response = await api.get("/customer/tickets");
    const tickets: ApiTicket[] = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
    return tickets.map(mapTicket);
};

export const getSupportTicketById = async (
    ticketId: string,
): Promise<SupportTicket | undefined> => {
    try {
        const [ticketRes, messagesRes] = await Promise.all([
            api.get(`/customer/tickets/${encodeURIComponent(ticketId)}`),
            api.get(
                `/customer/tickets/${encodeURIComponent(ticketId)}/messages`,
            ),
        ]);

        const ticket: ApiTicket = ticketRes.data?.data || ticketRes.data;
        const rawMessages: ApiMessage[] = Array.isArray(messagesRes.data)
            ? messagesRes.data
            : messagesRes.data?.data || [];

        return {
            ...mapTicket(ticket, 0),
            messages: rawMessages.map(mapMessage),
        };
    } catch (error: any) {
        if (error?.response?.status === 404) return undefined;
        throw error;
    }
};

export const createTicketApi = async (payload: {
    category: string;
    subject: string;
    severity: string;
    message: string;
}): Promise<{ ticketId: string }> => {
    const response = await api.post("/customer/tickets", payload);
    return response.data?.data || response.data;
};

export const replyToTicket = async (
    ticketId: string,
    message: string,
    attachments?: Array<{
        url: string;
        fileName: string;
        fileType: string;
        fileSize?: number;
    }>,
) => {
    const response = await api.post(
        `/customer/tickets/${encodeURIComponent(ticketId)}/messages`,
        { message, attachments },
    );
    return response.data?.data || response.data;
};

export const uploadTicketImage = async (
    ticketId: string,
    file: File,
): Promise<{ fileUrl: string; fileName: string }> => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await api.post(
        `/customer/tickets/${encodeURIComponent(ticketId)}/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data?.data || response.data;
};

// ── Form options ─────────────────────────────────────────────────

export const categoryOptions: SelectOption[] = [
    {
        value: "Payment",
        label: "Payment",
        hint: "Payment, refunds and transaction issues",
    },
    {
        value: "Orders",
        label: "Orders",
        hint: "Order, delivery and return support",
    },
    {
        value: "Account",
        label: "Account",
        hint: "Profile and account-related support",
    },
    {
        value: "KYC",
        label: "KYC",
        hint: "Verification and document support",
    },
    {
        value: "Wallet",
        label: "Wallet",
        hint: "Wallet, credit and debit support",
    },
    {
        value: "Incentives",
        label: "Incentives",
        hint: "Referral and earning questions",
    },
    {
        value: "Technical Issue",
        label: "Technical Issue",
        hint: "App, page or technical problem",
    },
    {
        value: "Other",
        label: "Other",
        hint: "Any other support request",
    },
];

export const subjectOptions: SelectOption[] = [
    { value: "Payment Pending", label: "Payment Pending" },
    { value: "Refund not received", label: "Refund not received" },
    { value: "Order delivery update", label: "Order delivery update" },
    { value: "Return / replacement", label: "Return / replacement" },
    { value: "Wallet balance mismatch", label: "Wallet balance mismatch" },
    { value: "Referral incentive query", label: "Referral incentive query" },
    { value: "KYC verification", label: "KYC verification" },
    { value: "Technical support", label: "Technical support" },
    { value: "Other request", label: "Other request" },
];

export const severityOptions: SelectOption[] = [
    {
        value: "Low",
        label: "Low",
        hint: "General question or low-priority request",
    },
    {
        value: "Medium",
        label: "Medium",
        hint: "Needs attention but is not blocking",
    },
    {
        value: "High",
        label: "High",
        hint: "Important issue affecting your activity",
    },
];

// ── Helpers ──────────────────────────────────────────────────────

export const formatSupportDate = (iso: string) =>
    iso
        ? new Date(iso).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : "-";

export const formatSupportTime = (iso: string) =>
    iso
        ? new Date(iso).toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
          })
        : "";

export const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;

    const kb = bytes / 1024;

    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
};

export const getAttachmentKind = (file: File): AttachmentKind => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    return "file";
};

export const makeAttachmentFromFile = (
    file: File,
): TicketAttachment => ({
    id: `${Date.now()}-${file.name}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    name: file.name,
    size: formatFileSize(file.size),
    kind: getAttachmentKind(file),
    url:
        file.type.startsWith("image/") ||
            file.type.startsWith("video/")
            ? URL.createObjectURL(file)
            : undefined,
});
